import React, { useState } from 'react';
import { FrontEdgeLogo } from '../../../../components/icons';
import Button from '../../../../components/button/Button';
import CloseConfirmation from './CloseConfirmation';
import { Spin, Steps, StepsProps } from 'antd';
import {
    ActiveOnboardingIcons,
    CompletedOnboardingIcons,
    InActiveOnboardingIcons
} from './OnboardingIcons';
import styled from 'styled-components';
import CompanyDetails from './CompanyDetails';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useUserProfile } from '../../../../customhooks/useUserProfile';
import { useInvestmentOptions } from '../../../../customhooks/investments/useInvestmentOptions';
import BankingDetails from './BankingDetails';
import InvestmentDocumentUploads from './InvestmentDocumentUploads';
import InvestmentSignatories from './InvestmentSignatories';
import { SettingOutlined } from '@ant-design/icons';
import { mutateConfig } from '../../../../api/queryBase';
import { AxiosError } from 'axios';
import { getErrorMessage, openNotification } from '../../../../utils/helper';
import { useGetQuery } from '../../../../customhooks/useGetQuery';

export const signatorySchema = Yup.object({
    firstName: Yup.string().required('First name is required.'),
    middleName: Yup.string().nullable(),
    lastName: Yup.string().required('Last name is required.'),
    email: Yup.string()
        .email('Enter a valid email address')
        .required('Email is required.'),
    phone: Yup.string()
        .required('Phone number is required.')
        .matches(/^\+?[0-9]{7,15}$/, 'Enter a valid phone number'),
    address: Yup.string().required('Address is required.'),
    bvn: Yup.string()
        .required('BVN is required.')
        .matches(/^[0-9]{11}$/, 'BVN must be 11 digits'),
    gender: Yup.string().required('Gender is required.'),
    dob: Yup.string().required('Date of Birth is required.'),
    passportUrl: Yup.string().required('Passport upload is required.'),
    utilityBillType: Yup.string().required('Utility bill type is required.'),
    utilityBillUrl: Yup.string().required('Utility bill document is required.'),
    identityType: Yup.string().required('Identification type is required.'),
    identificationUrl: Yup.string().required(
        'Identification document is required.'
    )
});

export const Onboarding = () => {
    const [step, setStep] = useState(0);

    const {
        entries: onboardingData,
        refetch,
        isFetching: fetchingOnboardingProgress
    } = useGetQuery<any>({
        url: '/investments/signup/progress',
        cacheKey: 'investmentOnboarding'
    });

    const { userDetails, isFetching } = useUserProfile();

    const mutation = mutateConfig({ method: 'post' });
    const { mutate, isLoading } = mutation({
        url: '/investments/signup/progress'
    });

    const { mutate: mutateLink, isLoading: loadingLinking } = mutation({
        url: '/investments/account/link'
    });

    const {
        investmentOptions,
        isFetching: fetchingOptions,
        products
    } = useInvestmentOptions();

    const [closeConfirmation, setCloseConfirmation] = useState(false);

    const stepList = [
        { component: <CompanyDetails {...{ investmentOptions, isLoading }} /> },
        {
            component: (
                <InvestmentSignatories {...{ investmentOptions, isLoading }} />
            )
        },
        {
            component: (
                <BankingDetails
                    {...{ investmentOptions, products, isLoading }}
                />
            )
        },
        {
            component: (
                <InvestmentDocumentUploads
                    {...{ isLoading: isLoading || loadingLinking }}
                />
            )
        }
    ];

    const customDot: StepsProps['progressDot'] = (dot, { status }) => (
        <>
            {status === 'process' ? (
                <ActiveOnboardingIcons />
            ) : status === 'finish' ? (
                <CompletedOnboardingIcons />
            ) : status === 'wait' ? (
                <InActiveOnboardingIcons />
            ) : (
                dot
            )}
        </>
    );

    const validationSchemas: Record<number, Yup.ObjectSchema<any>> = {
        0: Yup.object({
            phoneNumber: Yup.string()
                .required('Phone number is required.')
                .matches(/^\+?[0-9]{7,15}$/, 'Enter a valid phone number'),
            businessEmail: Yup.string()
                .email('Enter a valid email address')
                .required('Email is required.'),
            companyAddress: Yup.string()
                .required('Company address is required')
                .min(5, 'Address must be at least 5 characters'),
            tinNumber: Yup.string().required('TIN is required').min(8),
            companyType: Yup.number().required('Company type is required'),
            categoryOfBusiness: Yup.number().required(
                'Category of business is required'
            ),
            yearOfEstablishment: Yup.number()
                .typeError('Year must be a number')
                .required('Year is required')
                .min(1800)
                .max(new Date().getFullYear()),
            incomeRange: Yup.string().required(
                'Annual expected income is required'
            ),
            numberOfEmployees: Yup.number().required(
                'Number of employees is required'
            )
        }),

        1: Yup.object({
            signatories: Yup.array()
                .of(signatorySchema)
                .min(1, 'At least one signatory is required.')
        }),

        2: Yup.object({
            bankName: Yup.string().required('Bank is required'),
            accountNumber: Yup.string()
                .required('Account number is required')
                .matches(/^[0-9]+$/, 'Account must be numeric')
        }),

        3: Yup.object({
            documents: Yup.array()
                .of(
                    Yup.object({
                        url: Yup.string().required(
                            'Document upload is required'
                        ),
                        label: Yup.string().required(),
                        value: Yup.string().required()
                    })
                )
                .min(1, 'At least one document must be uploaded')
        })
    };

    const excluded = ['id_card', 'passport', 'utility_bill'];

    const initialValues = {
        ...onboardingData,
        companyName:
            onboardingData?.companyName ||
            userDetails?.corporate?.businessName ||
            '',
        businessEmail:
            onboardingData?.businessEmail ||
            userDetails?.corporate?.businessEmail ||
            '',
        phoneNumber:
            onboardingData?.phoneNumber ||
            userDetails?.corporate?.businessPhoneNumber ||
            '',
        yearOfEstablishment:
            onboardingData?.yearOfEstablishment ||
            userDetails?.corporate?.dateOfIncorporation ||
            '',
        tinNumber: onboardingData?.tinNumber || '',
        incomeRange: onboardingData?.incomeRange || '',
        companyType: onboardingData?.companyType || '',
        businessCategory: onboardingData?.businessCategory || '',
        companyAddress:
            onboardingData?.companyAddress ||
            userDetails?.corporate?.officeAddress ||
            '',
        town: onboardingData?.town || '',
        state: onboardingData?.state || '',
        country:
            onboardingData?.country ||
            userDetails?.corporate?.countryCode ||
            'NG',
        documents:
            onboardingData?.documents ||
            investmentOptions?.requiredDocuments?.filter(
                (doc: any) => !excluded.includes(doc.value)
            ) ||
            [],
        signatories: onboardingData?.signatories || [],
        productCode: onboardingData?.productCode || 'ARMMMF'
    };

    const handleSubmit = async (values: any) => {
        const transformedSignatories = values.signatories.map((sig: any) => ({
            firstName: sig.firstName,
            middleName: sig.middleName,
            lastName: sig.lastName,
            gender: sig.gender,
            dob: sig.dob,
            phone: sig.phone,
            address: sig.address,
            bvn: sig.bvn,
            email: sig.email,
            documents: [
              {
                type: "passport", 
                url: sig.passportUrl,
              },
              {
                type: "utility_bill",
                url: sig.utilityBillUrl,
              },
              {
                type: sig.identityType?.toLowerCase(),
                url: sig.identificationUrl,
              },
            ].filter(doc => !!doc.url), 
          }));

        mutate(values, {
            onSettled: async (_, error: AxiosError | undefined | any) => {
                if (error) {
                    openNotification(
                        'error',
                        getErrorMessage(error) || 'An error occurred, Try again'
                    );
                    return;
                }

                if (step < stepList.length - 1) {
                    openNotification('success', 'Step saved successfully');
                    refetch();
                    setStep((prev) => Math.min(prev + 1, stepList.length - 1));
                } else {
                    const payload = {
                        ...values,
                        signatories: transformedSignatories
                    }
                    mutateLink(
                        { payload, productCode: values.productCode },
                        {
                            onSettled: (
                                _,
                                linkError: AxiosError | undefined | any
                            ) => {
                                if (linkError) {
                                    openNotification(
                                        'error',
                                        getErrorMessage(linkError) ||
                                            'Failed to link investment account'
                                    );
                                    return;
                                }

                                openNotification(
                                    'success',
                                    'Onboarding completed!'
                                );
                            }
                        }
                    );
                }
            }
        });
    };

    return (
        <main className='w-[85vw] h-screen absolute top-0 bottom-0 flex flex-col mx-auto left-[10vw] '>
            <Spin
                fullscreen
                spinning={
                    isFetching || fetchingOptions || fetchingOnboardingProgress
                }
                delay={500}
                indicator={
                    <SettingOutlined
                        style={{ fontSize: 100, color: '#329FC1' }}
                        spin
                    />
                }
            />
            <header className='w-full flex justify-between items-center sticky bg-athensGray top-0 z-20 pb-4 border-b border-appcolorfaintnine '>
                <FrontEdgeLogo />

                <Button
                    text='Return to dashboard'
                    variant='secondary'
                    type='button'
                    onClick={() => setCloseConfirmation(true)}
                />
            </header>

            <div className='flex flex-1 mt-14 overflow-hidden relative'>
                <aside className='w-64 border-appcolorfaintnine bg-gray-50 sticky top-36 overflow-y-auto'>
                    <Container>
                        <Steps
                            progressDot={customDot}
                            current={step}
                            direction='vertical'
                            onChange={setStep}
                            className='h-full'
                            items={[
                                { title: 'Corporate Details' },
                                { title: 'Signatory Details' },
                                { title: 'Banking & Investment Setup' },
                                { title: 'Documents' }
                            ]}
                        />
                    </Container>
                </aside>

                <section className='flex-1 overflow-y-auto pl-10 pb-8'>
                    {!isFetching &&
                        userDetails &&
                        investmentOptions &&
                        !fetchingOptions &&
                        !fetchingOnboardingProgress && (
                            <Formik
                                initialValues={initialValues}
                                validationSchema={validationSchemas[step]}
                                onSubmit={handleSubmit}
                                validateOnBlur={false}
                                enableReinitialize
                            >
                                <Form>{stepList[step]?.component ?? step}</Form>
                            </Formik>
                        )}
                </section>
            </div>

            <CloseConfirmation
                open={closeConfirmation}
                onClose={() => setCloseConfirmation(false)}
            />
        </main>
    );
};

const Container = styled.div`
    height: 100%;
    max-height: 250px;
    .ant-steps-item > .ant-steps-item-container > .ant-steps-item-tail {
        padding: 16px 8px 8px !important;
    }
    .ant-steps-item-content {
        margin-left: 3rem;
        padding-top: 0.3rem;
        color: #344054;
        font-weight: 600;
        font-size: 14px;
    }
    .ant-steps-item-tail::after {
        width: 2px;
    }
`;
